import type { SimpleStreamRow } from "./bulkUpdateCamerasApi";

export type BulkSlicingMergedRow = {
  streamId: number;
  cameraId: number;
  cameraName: string;
  productLabel: string;
  currentSlice: boolean;
};

/** Normalize API shapes: nested camera object or legacy numeric camera id. */
export const getSimpleStreamCameraId = (row: SimpleStreamRow): number | undefined => {
  const cam = row.camera;
  if (typeof cam === "number" && Number.isFinite(cam)) {
    return cam;
  }
  if (cam && typeof cam === "object" && "id" in cam) {
    const id = (cam as { id?: number }).id;
    if (typeof id === "number" && Number.isFinite(id)) {
      return id;
    }
  }
  return undefined;
};

export const getSimpleStreamCameraName = (row: SimpleStreamRow): string => {
  const cam = row.camera;
  if (cam && typeof cam === "object" && "camera_name" in cam) {
    const name = (cam as { camera_name?: string }).camera_name;
    if (typeof name === "string" && name.length > 0) {
      return name;
    }
  }
  return "";
};

/**
 * Build display rows for bulk slicing: streams on selected cameras from /stream/simple/
 * plus human-readable product strings from /stream/product/.
 */
export const mergeBulkSlicingRows = (
  simpleRows: SimpleStreamRow[],
  streamIdToProductLabel: ReadonlyMap<number, string>,
  selectedCameraIds: number[],
  cameraIdToName?: ReadonlyMap<number, string>
): BulkSlicingMergedRow[] => {
  const selected = new Set(selectedCameraIds);
  const out: BulkSlicingMergedRow[] = [];

  for (const row of simpleRows) {
    const cameraId = getSimpleStreamCameraId(row);
    if (cameraId === undefined || !selected.has(cameraId)) {
      continue;
    }
    let cameraName = getSimpleStreamCameraName(row);
    if (!cameraName && cameraIdToName?.has(cameraId)) {
      cameraName = cameraIdToName.get(cameraId) ?? "";
    }
    const productLabel =
      streamIdToProductLabel.get(row.id) ??
      (typeof row.name === "string" && row.name.length > 0 ? row.name : "—");
    const currentSlice = row.slice === true;
    out.push({
      streamId: row.id,
      cameraId,
      cameraName,
      productLabel,
      currentSlice,
    });
  }

  out.sort((a, b) => {
    const nameCmp = a.cameraName.localeCompare(b.cameraName);
    if (nameCmp !== 0) return nameCmp;
    return a.productLabel.localeCompare(b.productLabel);
  });

  return out;
};

export const buildStreamIdToProductLabelMap = (
  productRowsByCamera: ReadonlyMap<number, ReadonlyArray<{ id: number; product?: string }>>
): Map<number, string> => {
  const map = new Map<number, string>();
  productRowsByCamera.forEach((rows) => {
    for (const r of rows) {
      if (typeof r.product === "string" && r.product.length > 0) {
        map.set(r.id, r.product);
      }
    }
  });
  return map;
};

export const partitionStreamIdsForSliceUpdate = (
  rows: BulkSlicingMergedRow[],
  desiredSliceByStreamId: Readonly<Record<number, boolean>>
): { enableIds: number[]; disableIds: number[] } => {
  const enableIds: number[] = [];
  const disableIds: number[] = [];

  for (const row of rows) {
    const desired = desiredSliceByStreamId[row.streamId];
    if (desired === undefined || desired === row.currentSlice) {
      continue;
    }
    if (desired) {
      enableIds.push(row.streamId);
    } else {
      disableIds.push(row.streamId);
    }
  }

  return { enableIds, disableIds };
};
